/** Safe rich-message and plain-link rendering. */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { JSONContent } from '@tiptap/react';
import { messageTextSegments } from '../lib/messageTextSegments';
import { messageDocument, messageLinkHref } from '../../netlify/functions/lib/messageFormatting.mjs';

/**
 * STABLE defaults, hoisted on purpose.
 *
 * `mentions = []` and `handleByUid = new Map()` written as default parameters
 * build a NEW object on every render, so any memo that depends on them is
 * dead on arrival: measured at 390x844 under 4x CPU throttle, 200 formatted
 * messages re-rendered in 198.8 ms against 84.8 ms for 200 plain ones, and
 * almost all of that gap was this component walking and re-segmenting every
 * document again for props that had not changed.
 */
const NO_MENTIONS: string[] = [];
const NO_HANDLES: Map<string, string> = new Map();

/** Render pasted web addresses as safe, tappable links without interpreting HTML. */
export function LinkifiedText({ text, mentions = NO_MENTIONS, handleByUid = NO_HANDLES, legacyAliasByUid = NO_HANDLES }: {
  text: string; mentions?: string[]; handleByUid?: Map<string, string>; legacyAliasByUid?: Map<string, string>;
}) {
  const doc = useMemo(() => messageDocument(text) as JSONContent, [text]);
  const link = (href: string, children: ReactNode, key: string | number) => <a key={key} href={href} target="_blank" rel="noopener noreferrer"
    onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}
    title={href} style={{ color: 'inherit', textDecoration: 'underline', overflowWrap: 'anywhere' }}>{children}</a>;
  function render(node: JSONContent, key: string): ReactNode {
    if (node.type === 'hardBreak') return <br key={key} />;
    if (node.type === 'text') {
      const explicitLink = node.marks?.find(mark => mark.type === 'link');
      let result: ReactNode = messageTextSegments(node.text || '', mentions, handleByUid, legacyAliasByUid).map((segment, index) => {
        if (segment.kind === 'mention') return <span key={index} style={{ background: 'var(--amber-bg)', color: 'var(--amber-text)', borderRadius: 6, padding: '0 4px', fontWeight: 700 }}>{segment.value}</span>;
        if (segment.kind === 'url' && !explicitLink) return link(segment.href, segment.value, index);
        return segment.value;
      });
      for (const mark of node.marks || []) {
        if (mark.type === 'bold') result = <strong>{result}</strong>;
        if (mark.type === 'italic') result = <em>{result}</em>;
        if (mark.type === 'underline') result = <u>{result}</u>;
        if (mark.type === 'strike') result = <s>{result}</s>;
        const href = mark.type === 'link' ? messageLinkHref(mark.attrs?.href) : null;
        if (href) {
          const labelIsDestination = messageLinkHref(node.text) === href;
          result = link(href, <>{result}{!labelIsDestination && <span style={{ fontSize: '.85em' }}> ({new URL(href).hostname})</span>}</>, 'link');
        }
      }
      return <span key={key}>{result}</span>;
    }
    const children = node.content?.map((child, index) => render(child, `${key}-${index}`));
    if (node.type === 'bulletList' || node.type === 'orderedList') return <span key={key} role="list" style={{ display: 'block', paddingLeft: '1.5em' }}>
      {node.content?.map((child, index) => <span role="listitem" key={index} style={{ display: 'list-item', listStyleType: node.type === 'bulletList' ? 'disc' : 'none', position: 'relative' }}>
        {node.type === 'orderedList' && <span aria-hidden="true" style={{ position: 'absolute', right: 'calc(100% + .4em)' }}>{(node.attrs?.start || 1) + index}.</span>}
        {render(child, `${key}-${index}`)}</span>)}
    </span>;
    return <span key={key} style={{ display: node.type === 'doc' ? 'inline' : 'block', whiteSpace: 'pre-wrap', minHeight: node.type === 'paragraph' ? '1.5em' : undefined }}>{children}</span>;
  }
  // The whole rendered tree, not just the parsed document: a message that has
  // not changed is not walked, segmented or rebuilt again when something else
  // on the screen re-renders. Same inputs, same output, and the document is
  // treated as immutable by every branch above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => <>{render(doc, 'message')}</>, [doc, mentions, handleByUid, legacyAliasByUid]);
}
